import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const API_URL = process.env.REACT_APP_API_URL || "";

const statusBadge = {
  NEW: "text-bg-primary",
  SEEN: "text-bg-secondary",
  IN_PROGRESS: "text-bg-warning",
  RESOLVED: "text-bg-success",
};

export default function AssistenzaManagementPage() {
  const nav = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedComm, setSelectedComm] = useState(null);
  const [reply, setReply] = useState("");
  const [replyImage, setReplyImage] = useState(null);
  const chatBoxRef = useRef(null);
  const [communications, setCommunications] = useState([]);
  const [commForm, setCommForm] = useState({
    title: "",
    message: "",
    question: "",
    questions_json: [],
    active: true,
    sort_order: 0,
  });

  async function loadTickets() {
    setLoading(true);
    try {
      const res = await authFetch(`${API_URL}/api/admin/beta/bugs/`);
      const data = await res.json();
        const ticketsData = Array.isArray(data) ? data : [];
        setTickets(ticketsData);
        setSelected((current) => {
          if (!current) return current;
          return ticketsData.find((x) => x.id === current.id) || current;
        });
    } catch (e) {
      console.error(e);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadCommunications() {
    try {
      const res = await authFetch(`${API_URL}/api/admin/beta/communications/`);
      const data = await res.json();
      setCommunications(
          (Array.isArray(data) ? data : []).filter((c) => {
            const title = String(c.title || "").toLowerCase();
            const question = String(c.question || "").toLowerCase();
            const hasQuestions = Array.isArray(c.questions_json)
              ? c.questions_json.length > 0
              : Boolean(c.questions_json);
            return !(
              title.includes("missione") ||
              question.includes("missione") ||
              hasQuestions ||
              Number(c.responses_count || 0) > 0
            );
          })
        );
    } catch (e) {
      console.error(e);
      setCommunications([]);
    }
  }

  useEffect(() => {
    loadTickets();
    loadCommunications();
    const timer = setInterval(loadTickets, 10000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!selected) return;
    const box = chatBoxRef.current;
    if (!box) return;
    setTimeout(() => {
      box.scrollTop = box.scrollHeight;
    }, 100);
  }, [selected?.id, selected?.messages?.length]);

  async function markTicketSeen(ticketId) {
    try {
      await authFetch(`${API_URL}/api/admin/beta/bugs/${ticketId}/seen/`, {
        method: "POST",
      });
      await loadTickets();
    } catch (e) {
      console.error(e);
    }
  }

  async function sendTyping(ticketId) {
    try {
      await authFetch(`${API_URL}/api/admin/beta/bugs/${ticketId}/typing/`, {
        method: "POST",
      });
    } catch (e) {
      console.error(e);
    }
  }

  function openTicket(ticket) {
    setSelected(ticket);
    setReply("");
    setReplyImage(null);
    markTicketSeen(ticket.id);
  }

  async function createCommunication(e) {
    e.preventDefault();

    const res = await authFetch(`${API_URL}/api/admin/beta/communications/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(commForm),
    });

    const data = await res.json();
    if (!res.ok) {
      alert(data.detail || "Errore creazione comunicazione assistenza.");
      return;
    }

    setCommForm({
      title: "",
      message: "",
      question: "",
      questions_json: [],
      active: true,
      sort_order: 0,
    });
    await loadCommunications();
  }

  function updateQuestion(index, patch) {
    const next = [...(commForm.questions_json || [])];
    next[index] = { ...next[index], ...patch };
    setCommForm({ ...commForm, questions_json: next });
  }

  function addQuestion() {
    setCommForm({
      ...commForm,
      questions_json: [
        ...(commForm.questions_json || []),
        { text: "", options: ["Sì", "No"] },
      ],
    });
  }

  function removeQuestion(index) {
    const next = (commForm.questions_json || []).filter((_, i) => i !== index);
    setCommForm({
      ...commForm,
      questions_json: next.length ? next : [{ text: "", options: ["Sì", "No"] }],
    });
  }

  function updateQuestionOption(qIndex, optIndex, value) {
    const next = [...(commForm.questions_json || [])];
    const options = [...(next[qIndex]?.options || [])];
    options[optIndex] = value;
    next[qIndex] = { ...next[qIndex], options };
    setCommForm({ ...commForm, questions_json: next });
  }

  function addQuestionOption(qIndex) {
    const next = [...(commForm.questions_json || [])];
    const options = [...(next[qIndex]?.options || []), ""];
    next[qIndex] = { ...next[qIndex], options };
    setCommForm({ ...commForm, questions_json: next });
  }

  function removeQuestionOption(qIndex, optIndex) {
    const next = [...(commForm.questions_json || [])];
    const options = (next[qIndex]?.options || []).filter((_, i) => i !== optIndex);
    next[qIndex] = { ...next[qIndex], options: options.length ? options : [""] };
    setCommForm({ ...commForm, questions_json: next });
  }

  async function toggleCommunication(comm) {
    const res = await authFetch(`${API_URL}/api/admin/beta/communications/${comm.id}/`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ active: !comm.active }),
    });

    const data = await res.json();
    if (!res.ok) {
      alert(data.detail || "Errore aggiornamento comunicazione assistenza.");
      return;
    }

    await loadCommunications();
  }

  async function sendTicketReply() {
    if (!selected) return;

    const text = reply.trim();
    if (!text && !replyImage) {
      alert("Scrivi un messaggio o allega un'immagine.");
      return;
    }

    const fd = new FormData();
    fd.append("message", text);
    if (replyImage) fd.append("image", replyImage);

    const res = await authFetch(`${API_URL}/api/admin/beta/bugs/${selected.id}/messages/`, {
      method: "POST",
      body: fd,
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      alert(data.detail || "Errore invio messaggio.");
      return;
    }

    if (data.report) {
      setSelected(data.report);
      setTickets((prev) => prev.map((x) => x.id === data.report.id ? data.report : x));
    }

    setReply("");
    setReplyImage(null);
    const replyInput = document.getElementById("admin-ticket-reply-image");
    if (replyInput) replyInput.value = "";
    await loadTickets();
  }

  async function updateTicket(payload) {
    if (!selected) return;

    const res = await authFetch(`${API_URL}/api/admin/beta/bugs/${selected.id}/`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok) {
      alert(data.detail || "Errore aggiornamento segnalazione.");
      return;
    }

    setSelected(data.report);
    await loadTickets();
  }

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <div className="text-muted small text-uppercase fw-semibold">
            Gestione App Clienti
          </div>
          <h1 className="fw-bold mb-1">🧪 Centro Assistenza</h1>
          <div className="text-muted">
            Segnalazioni, screenshot e risposte amministratore dai clienti dell'app cliente.
          </div>
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary rounded-pill px-4"
          onClick={() => nav("/gestione-app-clienti")}
        >
          ← Gestione App Clienti
        </button>
      </div>

      <div className="card border-0 shadow rounded-4">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0">Ticket ricevuti</h5>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={loadTickets}>
              Aggiorna
            </button>
          </div>

          {loading ? (
            <div className="text-muted">Caricamento...</div>
          ) : tickets.length === 0 ? (
            <div className="text-muted">Nessuna segnalazione assistenza trovata.</div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Data</th>
                    <th>Cliente</th>
                    <th>Area</th>
                    <th>Titolo</th>
                    <th>Stato</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr key={ticket.id}>
                      <td>#{ticket.id}</td>
                      <td>{ticket.created_at ? new Date(ticket.created_at).toLocaleString("it-IT") : "-"}</td>
                      <td>{ticket.client?.name || ticket.user?.email || "-"}</td>
                      <td>{ticket.area || "-"}</td>
                        <td className="fw-semibold">
                          <div className="d-flex flex-column gap-1">
                            <span>{ticket.title}</span>
                            {Number(ticket.support_unread_count || 0) > 0 && (
                              <span className="badge text-bg-danger rounded-pill align-self-start">
                                💬 {ticket.support_unread_count} nuovo
                              </span>
                            )}
                          </div>
                        </td>
                      <td>
                        <span className={`badge ${statusBadge[ticket.status] || "text-bg-light"}`}>
                          {ticket.status_label || ticket.status}
                        </span>
                      </td>
                      <td className="text-end">
                        <button type="button" className="btn btn-sm btn-success" onClick={() => openTicket(ticket)}>
                          Apri
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card border-0 shadow rounded-4 mt-4">
        <div className="card-body p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h5 className="fw-bold mb-1">Comunicazioni Assistenza</h5>
              <div className="text-muted small">
                Comunicazioni e avvisi visibili nel Centro Assistenza dell'app cliente.
              </div>
            </div>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={loadCommunications}>
              Aggiorna comunicazioni
            </button>
          </div>

          <form className="row g-3 mb-4" onSubmit={createCommunication}>
            <div className="col-12 col-lg-4">
              <label className="form-label fw-semibold">Titolo</label>
              <input
                className="form-control"
                value={commForm.title}
                onChange={(e) => setCommForm({ ...commForm, title: e.target.value })}
                placeholder="Es. Aggiornamento app"
              />
            </div>

            <div className="col-12 col-lg-4">
              <label className="form-label fw-semibold">Domanda principale breve</label>
              <input
                className="form-control"
                value={commForm.question}
                onChange={(e) => setCommForm({ ...commForm, question: e.target.value })}
                placeholder=""
              />
              <div className="small text-muted mt-1">
                
              </div>
            </div>

            <div className="col-12 col-lg-2">
              <label className="form-label fw-semibold">Ordine</label>
              <input
                type="number"
                className="form-control"
                value={commForm.sort_order}
                onChange={(e) => setCommForm({ ...commForm, sort_order: e.target.value })}
              />
            </div>

            <div className="col-12 col-lg-2 d-flex align-items-end">
              <div className="form-check">
                <input
                  id="commActive"
                  type="checkbox"
                  className="form-check-input"
                  checked={commForm.active}
                  onChange={(e) => setCommForm({ ...commForm, active: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="commActive">
                  Attiva
                </label>
              </div>
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold">Messaggio / istruzioni cliente</label>
              <textarea
                className="form-control"
                rows="4"
                value={commForm.message}
                onChange={(e) => setCommForm({ ...commForm, message: e.target.value })}
                placeholder="Scrivi una comunicazione o istruzione per i clienti..."
              />
            </div>

            <div className="col-12">
              <div className="border rounded-4 p-3 bg-light d-none">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <div className="fw-bold">📋 Sondaggio / domande missione</div>
                    <div className="small text-muted">
                      Ogni clienti compilerà il proprio sondaggio. Se hai 10 clienti, vedrai 10 risposte separate.
                    </div>
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={addQuestion}>
                    + Aggiungi domanda
                  </button>
                </div>

                {(commForm.questions_json || []).map((q, qIndex) => (
                  <div className="card border-0 shadow-sm rounded-4 mb-3" key={qIndex}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between gap-2 mb-2">
                        <label className="form-label fw-semibold mb-0">
                          Domanda {qIndex + 1}
                        </label>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeQuestion(qIndex)}
                        >
                          Rimuovi
                        </button>
                      </div>

                      <input
                        className="form-control mb-3"
                        value={q.text}
                        onChange={(e) => updateQuestion(qIndex, { text: e.target.value })}
                        placeholder="Es. Il credito Wallet si è aggiornato correttamente?"
                      />

                      <div className="small fw-semibold mb-2">Opzioni risposta</div>
                      {(q.options || []).map((opt, optIndex) => (
                        <div className="input-group mb-2" key={optIndex}>
                          <span className="input-group-text">◯</span>
                          <input
                            className="form-control"
                            value={opt}
                            onChange={(e) => updateQuestionOption(qIndex, optIndex, e.target.value)}
                            placeholder="Es. Sì"
                          />
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => removeQuestionOption(qIndex, optIndex)}
                          >
                            ×
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => addQuestionOption(qIndex)}
                      >
                        + Aggiungi opzione
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="col-12">
              <button type="submit" className="btn btn-success rounded-pill px-4">
                Crea comunicazione assistenza
              </button>
            </div>
          </form>

          {communications.length === 0 ? (
            <div className="text-muted">Nessuna comunicazione assistenza creata.</div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Titolo</th>
                    <th>Domanda</th>
                    <th>Risposte</th>
                    <th>Stato</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {communications.map((comm) => (
                    <tr key={comm.id}>
                      <td>#{comm.id}</td>
                      <td>
                          <button
                            type="button"
                            className="btn btn-link p-0 text-start fw-semibold text-decoration-none"
                            onClick={() => setSelectedComm(comm)}
                          >
                            {comm.title}
                          </button>
                        <div className="small text-muted text-truncate" style={{ maxWidth: 420 }}>
                          {comm.message}
                        </div>
                      </td>
                      <td>
                        <div>{comm.question || "-"}</div>
                        {(comm.questions_json || []).length > 0 && (
                          <div className="small text-muted">
                            {(comm.questions_json || []).length} domande
                          </div>
                        )}
                      </td>
                      <td>{comm.responses_count || 0}</td>
                      <td>
                        <span className={`badge ${comm.active ? "text-bg-success" : "text-bg-secondary"}`}>
                          {comm.active ? "Attiva" : "Disattivata"}
                        </span>
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className={`btn btn-sm ${comm.active ? "btn-outline-danger" : "btn-outline-success"}`}
                          onClick={() => toggleCommunication(comm)}
                        >
                          {comm.active ? "Disattiva" : "Attiva"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

        {selectedComm && (
          <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.45)" }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content rounded-4">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title fw-bold mb-1">{selectedComm.title}</h5>
                    <div className="text-muted small">
                      {(selectedComm.questions_json || []).length} domande · {selectedComm.responses_count || 0} risposte clienti
                    </div>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setSelectedComm(null)}></button>
                </div>

                <div className="modal-body">
                  <div className="mb-4">
                    <div className="small text-muted fw-semibold">Messaggio</div>
                    <div style={{ whiteSpace: "pre-wrap" }}>{selectedComm.message}</div>
                  </div>

                  <div className="mb-4">
                    <div className="fw-bold mb-2">📋 Domande sondaggio</div>
                    {(selectedComm.questions_json || []).length === 0 ? (
                      <div className="text-muted">Nessuna domanda configurata.</div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {(selectedComm.questions_json || []).map((q, qIndex) => (
                          <div key={qIndex} className="border rounded-4 p-3">
                            <div className="fw-semibold">{qIndex + 1}. {q.text}</div>
                            <div className="small text-muted">
                              Opzioni: {(q.options || []).join(" · ")}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="fw-bold mb-2">Risposte clienti</div>
                    {(selectedComm.responses || []).length === 0 ? (
                      <div className="alert alert-light rounded-4 mb-0">
                        Nessun clienti ha ancora risposto a questa missione.
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-3">
                        {(selectedComm.responses || []).map((r) => (
                          <div key={r.id} className="border rounded-4 p-3">
                            <div className="d-flex justify-content-between gap-2 mb-2">
                              <div className="fw-semibold">{r.client || r.user || "Clienti"}</div>
                              <div className="small text-muted">
                                {r.created_at ? new Date(r.created_at).toLocaleString("it-IT") : ""}
                              </div>
                            </div>

                            {(selectedComm.questions_json || []).map((q, qIndex) => (
                              <div key={qIndex} className="small mb-2">
                                <div className="fw-semibold">{q.text}</div>
                                <div>{r.answers_json?.[qIndex] || "-"}</div>
                              </div>
                            ))}

                            {r.comment && (
                              <div className="small mt-2">
                                <strong>Commento:</strong> {r.comment}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setSelectedComm(null)}>
                    Chiudi
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      {selected && (
        <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.45)" }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content rounded-4">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title fw-bold mb-1">#{selected.id} - {selected.title}</h5>
                  <div className="text-muted small">{selected.client?.name || selected.user?.email || "Cliente non collegato"}</div>
                </div>
                <button type="button" className="btn-close" onClick={() => setSelected(null)}></button>
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-12 col-lg-7">
                    <div className="mb-3">
                      <div className="small text-muted fw-semibold">Area</div>
                      <div>{selected.area || "-"}</div>
                    </div>

                    <div className="mb-3">
                      <div className="small text-muted fw-semibold">Descrizione</div>
                      <div style={{ whiteSpace: "pre-wrap" }}>{selected.description}</div>
                    </div>

                    <div className="mb-3">
                      <div className="small text-muted fw-semibold">URL pagina</div>
                      <div className="text-break">{selected.page_url || "-"}</div>
                    </div>

                    <div className="mb-3">
                      <div className="small text-muted fw-semibold">Browser / dispositivo</div>
                      <div className="text-break small">{selected.user_agent || "-"}</div>
                    </div>

                    <div className="mb-3">
                      <div className="small text-muted fw-semibold mb-2">Screenshot</div>
                      {selected.screenshots?.length ? (
                        <div className="row g-2">
                          {selected.screenshots.map((ss) => (
                            <div className="col-6 col-md-4" key={ss.id}>
                              <a href={ss.image_url} target="_blank" rel="noreferrer">
                                <img src={ss.image_url} alt="Screenshot ticket" className="img-fluid rounded border" />
                              </a>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-muted">Nessuno screenshot allegato.</div>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-lg-5">
                    <div className="card border-0 bg-light rounded-4">
                      <div className="card-body">
                        <div className="fw-bold mb-2">Stato lavorazione</div>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <button className="btn btn-sm btn-outline-primary" onClick={() => updateTicket({ status: "NEW" })}>Nuovo</button>
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => updateTicket({ status: "SEEN" })}>Visto</button>
                          <button className="btn btn-sm btn-outline-warning" onClick={() => updateTicket({ status: "IN_PROGRESS" })}>In lavorazione</button>
                          <button className="btn btn-sm btn-outline-success" onClick={() => updateTicket({ status: "RESOLVED" })}>Risolto</button>
                        </div>

                          <div className="fw-bold mb-2">💬 Conversazione</div>

                          <div ref={chatBoxRef} className="border rounded-4 p-3 mb-3 bg-white" style={{ maxHeight: 360, overflowY: "auto" }}>
                            {(selected.messages || []).length === 0 ? (
                              <div className="text-muted small">Nessun messaggio.</div>
                            ) : (
                              selected.messages.map((m) => (
                                <div key={m.id} className={`mb-3 d-flex ${m.sender_type === "ADMIN" ? "justify-content-end" : "justify-content-start"}`}>
                                  <div className={`rounded-4 px-3 py-2 ${m.sender_type === "ADMIN" ? "bg-success text-white" : "bg-light border"}`} style={{ maxWidth: "85%" }}>
                                    <div className="small fw-bold mb-1" style={{ opacity: 0.8 }}>{m.sender_name}</div>
                                      {m.message && <div style={{ whiteSpace: "pre-wrap" }}>{m.message}</div>}
                                      {m.image_url && (
                                        <a href={m.image_url} target="_blank" rel="noreferrer" className="d-block mt-2">
                                          <img src={m.image_url} alt="Allegato messaggio" className="img-fluid rounded-3 border" style={{ maxHeight: 220 }} />
                                        </a>
                                      )}
                                    <div className="small mt-2" style={{ opacity: 0.7 }}>{m.created_at ? new Date(m.created_at).toLocaleString("it-IT") : ""}</div>
                                  </div>
                                </div>
                              ))
                            )}
                              {selected.client_is_typing && (
                                <div className="small text-muted fst-italic mt-2">
                                  👤 {selected.reporter_first_name || selected.client?.name || "Il cliente"} sta scrivendo...
                                </div>
                              )}
                          </div>

                          <textarea
                            className="form-control mb-2"
                            rows="4"
                            value={reply}
                              onChange={(e) => {
                                setReply(e.target.value);
                                if (selected) sendTyping(selected.id);
                              }}
                            placeholder="Scrivi un messaggio al cliente..."
                          />

                              <label className="form-label fw-semibold">Allega immagine o screenshot</label>
                              <input
                                id="admin-ticket-reply-image"
                                type="file"
                                accept="image/*"
                                className="form-control mb-2"
                                onChange={(e) => setReplyImage(e.target.files?.[0] || null)}
                              />

                              {replyImage && (
                                <div className="alert alert-light rounded-4 d-flex align-items-center justify-content-between gap-3 mb-3">
                                  <div className="d-flex align-items-center gap-3">
                                    <img
                                      src={URL.createObjectURL(replyImage)}
                                      alt="Anteprima allegato"
                                      className="rounded-3 border"
                                      style={{ width: 72, height: 72, objectFit: "cover" }}
                                    />
                                    <div>
                                      <div className="fw-semibold">Immagine selezionata</div>
                                      <div className="text-muted small">{replyImage.name}</div>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => {
                                      setReplyImage(null);
                                      const input = document.getElementById("admin-ticket-reply-image");
                                      if (input) input.value = "";
                                    }}
                                  >
                                    Rimuovi
                                  </button>
                                </div>
                              )}

                          <button className="btn btn-success w-100 mb-3" onClick={sendTicketReply}>
                            Invia messaggio
                          </button>

                        <div className="fw-bold mb-2">Esito risoluzione gestionale</div>
                        <div className="d-flex gap-2">
                          <button className="btn btn-sm btn-success" onClick={() => updateTicket({ resolved_feedback: "YES" })}>
                            Ticket risolto
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => updateTicket({ resolved_feedback: "NO" })}>
                            Ticket non risolto
                          </button>
                        </div>

                        {selected.resolved_feedback_label && (
                          <div className="alert alert-info mt-3 mb-0">
                            Esito attuale: {selected.resolved_feedback_label}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setSelected(null)}>
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
